import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import {
    CloverOAuthCompletionResult,
    type CloverOAuthCompletion,
} from '@pillar-portal/api-types/ipg/clover/heartbeat';
import {
    describeCloverOAuthCompletion,
    subscribeToCloverOAuthCompletion,
} from '@/lib/clover/oauth-completion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatTimestampForMerchantUi, type LosslessNumber } from '@pillar-portal/api-types';
import {
    CloverOAuthAuthorizationState,
    CloverOAuthRefreshIntervalMinutes,
    CloverOAuthRefreshScheduleState,
    CloverPaymentsInfrastructureState,
    CloverPaymentsInfrastructureTerminalKind,
    CloverReadinessCheckStatus,
    type CloverOAuthAuthorizationStatus,
    type CloverPaymentsInfrastructureStatus,
    type CloverPaymentsInfrastructureSynchronizationResult,
    type CloverPaymentsInfrastructureTerminal,
    type CloverReadinessCheck,
    type CloverReadinessResult,
    type CloverSurchargeReadiness,
    type RenewCloverOAuthAuthorizationResponse,
} from '@pillar-portal/api-types/ipg/clover/heartbeat';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ApiError, paymentsInfrastructureApi } from '@/lib/pillarApi';
import { CloverInvalidationConfirmationDialog } from './CloverInvalidationConfirmationDialog';
import { CloverOAuthConfirmationDialog } from './CloverOAuthConfirmationDialog';

export interface CloverReadinessCardProps {
    readonly organizationUuid: string;
    readonly organizationName: string | null;
    readonly portalUrl: string;
    readonly renewalEnabled: boolean;
    readonly invalidationEnabled: boolean;
    readonly authorizationNavigationEnabled: boolean;
    readonly terminalSelectionEnabled: boolean;
    readonly readinessEnabled: boolean;
    readonly refreshIntervalEditingEnabled?: boolean;
    readonly unavailableReason?: string;
}

function formatOAuthExpiration(
    expiresTimestamp: string | null,
    merchantTimeZone: CloverOAuthAuthorizationStatus['merchant_timezone']
): string {
    if (expiresTimestamp === null) {
        return 'Not reported by Clover';
    }
    const expiresAt = new Date(expiresTimestamp);
    const remainingMilliseconds = expiresAt.getTime() - Date.now();
    const absolute = formatTimestampForMerchantUi(expiresTimestamp, merchantTimeZone);
    if (remainingMilliseconds <= 0) {
        return `${absolute} (expired)`;
    }
    const remainingHours = Math.ceil(remainingMilliseconds / (60 * 60 * 1000));
    const eta =
        remainingHours < 48
            ? `${remainingHours} hour${remainingHours === 1 ? '' : 's'}`
            : `${Math.ceil(remainingHours / 24)} days`;
    return `${absolute} (in ${eta})`;
}

const CloverReadinessCheckDefinitions = [
    { key: 'credential', label: 'Credentials' },
    { key: 'ecommerceKey', label: 'Ecommerce' },
    { key: 'cloudPayDevice', label: 'Cloud Pay' },
    { key: 'multiPay', label: 'Reusable tokens' },
] as const satisfies ReadonlyArray<{
    readonly key: keyof CloverReadinessResult;
    readonly label: string;
}>;

/** Plain words for each Clover connection state; the raw enum is never shown (BUG-278). */
const CloverOAuthAuthorizationStateLabels: Readonly<Record<CloverOAuthAuthorizationState, string>> =
    {
        [CloverOAuthAuthorizationState.NOT_CONNECTED]: 'Not connected',
        [CloverOAuthAuthorizationState.ACTIVE]: 'Connected',
        [CloverOAuthAuthorizationState.REAUTHORIZATION_REQUIRED]: 'Clover sign-in required',
        [CloverOAuthAuthorizationState.DISCONNECTED]: 'Disconnected',
    };

/** Counts virtual and physical terminals together (BUG-255). */
/**
 * The server's own explanation, never the transport fallback (which can carry
 * a raw HTTP status or response body, BUG-257).
 */
function cloverServerMessage(error: Error | null): string | undefined {
    return error instanceof ApiError ? error.responseMessage : undefined;
}

function countTerminalChange(
    synchronization: CloverPaymentsInfrastructureSynchronizationResult,
    change: 'created' | 'restored' | 'removed'
): number {
    switch (change) {
        case 'created':
            return (
                synchronization.created_virtual_terminal_count +
                synchronization.created_physical_terminal_count
            );
        case 'restored':
            return (
                synchronization.restored_virtual_terminal_count +
                synchronization.restored_physical_terminal_count
            );
        case 'removed':
            return (
                synchronization.removed_virtual_terminal_count +
                synchronization.removed_physical_terminal_count
            );
    }
}

/** Names the Clover devices a refresh could not set up, without any raw cause (BUG-270 D3). */
function describeFailedCloverDevices(serials: readonly string[]): string {
    const devices =
        serials.length === 1
            ? `Clover device ${serials[0]} could not be set up.`
            : `Clover devices ${serials.join(', ')} could not be set up.`;
    return `${devices} The other terminals are ready. Use Refresh Clover Connection to try again; if it keeps failing, contact support.`;
}

/**
 * PILLAR's record row (as in the organization lists) with the shared selected
 * state of its data rows (`data-[state=selected]:bg-muted`, components/ui/table).
 */
const CLOVER_TERMINAL_RECORD_CLASS_NAME =
    'w-full text-left p-4 border rounded-lg hover:bg-gray-50 transition-colors data-[state=selected]:bg-muted';

// A named terminal shows its name alone; the serial stays a separate detail
// (Terminal display names). An unnamed device falls back to its serial.
function getCloverTerminalLabel(terminal: CloverPaymentsInfrastructureTerminal): string {
    const name = terminal.name?.trim();
    const configuredName = name !== undefined && name.length > 0 ? name : null;
    switch (terminal.kind) {
        case CloverPaymentsInfrastructureTerminalKind.VIRTUAL:
            return `Virtual terminal: ${configuredName ?? 'Unnamed terminal'}`;
        case CloverPaymentsInfrastructureTerminalKind.PHYSICAL:
            return `Device: ${configuredName ?? terminal.serial}`;
    }
}

function CloverReadinessCheckSummary(props: {
    readonly check: CloverReadinessCheck;
    readonly label: string;
}): ReactElement {
    const statusLabel: string = (() => {
        switch (props.check.status) {
            case CloverReadinessCheckStatus.READY:
                return 'Ready';
            case CloverReadinessCheckStatus.UNVERIFIED:
                return 'Not verified';
            case CloverReadinessCheckStatus.NOT_READY:
                return 'Needs attention';
        }
    })();
    return (
        <div className="rounded border p-2" data-readiness-check={props.label}>
            <p className="text-sm font-medium">{props.label}</p>
            <p className="text-sm">{statusLabel}</p>
            {props.check.status !== CloverReadinessCheckStatus.READY && (
                <p className="text-sm text-muted-foreground">{props.check.message}</p>
            )}
        </div>
    );
}

function formatCloverSurchargeRate(rate: LosslessNumber): string {
    if (/^1(?:\.0+)?$/u.test(rate.value)) {
        return '100%';
    }
    const fraction = (rate.value.split('.')[1] ?? '').padEnd(2, '0');
    const wholePercent = String(Number(fraction.slice(0, 2)));
    const fractionalPercent = fraction.slice(2).replace(/0+$/u, '');
    return `${wholePercent}${fractionalPercent.length === 0 ? '' : `.${fractionalPercent}`}%`;
}

function CloverSurchargeReadinessSummary(props: {
    readonly surchargeReadiness: CloverSurchargeReadiness;
}): ReactElement {
    const configuration = props.surchargeReadiness.configuration;
    return (
        <div className="rounded border p-2" data-readiness-check="CNP surcharge" role="status">
            <p className="text-sm font-medium">CNP surcharge</p>
            <p className="text-sm">
                {props.surchargeReadiness.check.status === CloverReadinessCheckStatus.READY
                    ? 'Ready'
                    : 'Needs attention'}
            </p>
            {props.surchargeReadiness.check.status !== CloverReadinessCheckStatus.READY && (
                <p className="text-sm text-muted-foreground">
                    {props.surchargeReadiness.check.message}
                </p>
            )}
            {configuration?.supported === true && (
                <p className="text-sm text-muted-foreground">
                    {`Clover may apply a surcharge of up to ${formatCloverSurchargeRate(configuration.rate)} to eligible credit card transactions. The exact surcharge is determined after card entry.`}
                </p>
            )}
            {configuration?.supported === false && (
                <p className="text-sm text-muted-foreground">
                    Clover surcharging is disabled for this merchant.
                </p>
            )}
        </div>
    );
}

function CloverPaymentsInfrastructureSummary({
    status,
    isError,
    synchronizing,
    synchronization,
    synchronizationError,
    readinessCheckEnabled,
    checkedTerminalDeviceUuid,
    onCheckReadiness,
}: {
    readonly status: CloverPaymentsInfrastructureStatus | undefined;
    readonly isError: boolean;
    readonly synchronizing: boolean;
    readonly synchronization: CloverPaymentsInfrastructureSynchronizationResult | undefined;
    readonly synchronizationError: Error | null;
    readonly readinessCheckEnabled: boolean;
    readonly checkedTerminalDeviceUuid: string | null;
    readonly onCheckReadiness: (terminalDeviceUuid: string) => void;
}): ReactElement {
    return (
        <div className="space-y-1 rounded border p-2" data-clover-payments-infrastructure="true">
            <p className="text-sm font-medium">Clover terminals</p>
            {isError ? (
                <p className="text-sm text-destructive" role="alert">
                    Unable to load the Clover terminals.
                </p>
            ) : status === undefined ? (
                <p className="text-sm" role="status">
                    Loading Clover terminals...
                </p>
            ) : status.state === CloverPaymentsInfrastructureState.MERCHANT_ACCOUNT_CONFLICT ? (
                // Several pre-existing Clover merchant accounts: provisioning asks for
                // help instead of choosing one (FEAT-317).
                <div
                    className="space-y-1 rounded border border-destructive bg-destructive/10 p-2 text-sm"
                    role="alert"
                    data-clover-merchant-account-conflict="true"
                >
                    <p className="font-medium">
                        Help needed: more than one Clover merchant account
                    </p>
                    <p>
                        This organization already has {status.conflicting_merchant_accounts.length}{' '}
                        Clover merchant accounts, and none of them matches the Clover account that
                        was connected, so Anchorbase did not choose one and set up no Clover
                        terminals. Contact customer support to remove or correct the extra accounts,
                        then choose Refresh Clover Connection.
                    </p>
                    <ul className="list-disc pl-5">
                        {status.conflicting_merchant_accounts.map((merchantAccount) => (
                            <li key={merchantAccount.merchant_account_uuid}>
                                {merchantAccount.name ?? 'Unnamed merchant account'} (merchant ID{' '}
                                {merchantAccount.processor_mid})
                            </li>
                        ))}
                    </ul>
                </div>
            ) : status.state === CloverPaymentsInfrastructureState.PROVISIONED ? (
                <>
                    <p className="text-sm">
                        Clover merchant: {status.clover_merchant_name ?? 'Name not reported'} (
                        {status.clover_merchant_id})
                    </p>
                    <p className="text-sm">
                        Physical terminals: {status.physical_terminal_count}. Virtual terminals:{' '}
                        {status.virtual_terminal_count}.
                    </p>
                    {status.physical_terminal_count === 0 && (
                        <p className="text-sm text-muted-foreground">
                            Clover reported no devices for this merchant.
                        </p>
                    )}
                    <ul className="space-y-2" data-clover-terminal-list="true">
                        {status.terminals.map((terminal) => {
                            const label = getCloverTerminalLabel(terminal);
                            const selected =
                                checkedTerminalDeviceUuid === terminal.terminal_device_uuid;
                            const details = (
                                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                                    <span className="font-medium">{label}</span>
                                    {terminal.kind ===
                                        CloverPaymentsInfrastructureTerminalKind.PHYSICAL && (
                                        <span className="text-muted-foreground">
                                            Serial: {terminal.serial}
                                        </span>
                                    )}
                                    {readinessCheckEnabled && (
                                        <span className="text-xs text-muted-foreground">
                                            {selected ? 'Readiness shown below' : 'Check readiness'}
                                        </span>
                                    )}
                                </div>
                            );
                            return (
                                <li
                                    key={terminal.terminal_device_uuid}
                                    data-clover-terminal-device-uuid={terminal.terminal_device_uuid}
                                >
                                    {readinessCheckEnabled ? (
                                        // A terminal row is a selectable record, as in PILLAR's
                                        // record lists; the selected one owns the readiness panel
                                        // (BUG-273).
                                        <button
                                            type="button"
                                            className={CLOVER_TERMINAL_RECORD_CLASS_NAME}
                                            data-state={selected ? 'selected' : undefined}
                                            aria-current={selected ? 'true' : undefined}
                                            aria-label={`Check readiness for ${label}`}
                                            onClick={() =>
                                                onCheckReadiness(terminal.terminal_device_uuid)
                                            }
                                        >
                                            {details}
                                        </button>
                                    ) : (
                                        <div className={CLOVER_TERMINAL_RECORD_CLASS_NAME}>
                                            {details}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                    {!status.principal_assigned && (
                        <p className="text-sm text-muted-foreground" role="note">
                            This organization has no Principal yet. When one is assigned, use
                            Refresh Clover Connection so they can take Clover payments.
                        </p>
                    )}
                    {status.merchant_name_mismatch && (
                        <div
                            className="rounded border border-yellow-400 bg-yellow-50 p-2 text-sm text-yellow-900"
                            role="status"
                            data-clover-merchant-name-warning="true"
                        >
                            The Clover merchant name &quot;{status.clover_merchant_name}&quot;
                            differs from this organization&apos;s name &quot;
                            {status.organization_name}
                            &quot;. Customer support has been notified. Check that the intended
                            Clover account is connected.
                        </div>
                    )}
                </>
            ) : synchronizing ? (
                <p className="text-sm" role="status">
                    Setting up Clover terminals...
                </p>
            ) : (
                <p className="text-sm text-muted-foreground">
                    Clover terminals are not set up yet. Use Refresh Clover Connection to set them
                    up.
                </p>
            )}
            {synchronization !== undefined && !synchronizing && (
                <p className="text-sm text-muted-foreground" role="status">
                    Last refresh: {countTerminalChange(synchronization, 'created')} added,{' '}
                    {countTerminalChange(synchronization, 'restored')} restored,{' '}
                    {countTerminalChange(synchronization, 'removed')} removed.
                </p>
            )}
            {synchronization !== undefined &&
                !synchronizing &&
                synchronization.failed_physical_device_serials.length > 0 && (
                    <p className="text-sm text-destructive" role="alert">
                        {describeFailedCloverDevices(
                            synchronization.failed_physical_device_serials
                        )}
                    </p>
                )}
            {synchronizationError !== null && (
                <p className="text-sm text-destructive" role="alert">
                    {cloverServerMessage(synchronizationError) ??
                        'Unable to refresh the Clover terminals.'}{' '}
                    The Clover connection was not changed.
                </p>
            )}
        </div>
    );
}

export function CloverReadinessCard({
    organizationUuid,
    organizationName,
    renewalEnabled,
    invalidationEnabled,
    authorizationNavigationEnabled,
    terminalSelectionEnabled,
    readinessEnabled,
    refreshIntervalEditingEnabled = false,
    unavailableReason,
}: CloverReadinessCardProps): ReactElement {
    const queryClient = useQueryClient();
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [invalidationConfirmationOpen, setInvalidationConfirmationOpen] = useState(false);
    const [selectedTerminalDeviceUuid, setSelectedTerminalDeviceUuid] = useState<string | null>(
        null
    );
    useEffect(() => {
        setSelectedTerminalDeviceUuid(null);
    }, [organizationUuid]);
    // The Clover connection window reports its result here, and the card
    // refreshes from the server instead of trusting the message (BUG-278).
    const [oauthCompletion, setOAuthCompletion] = useState<CloverOAuthCompletion | null>(null);
    useEffect(
        () =>
            subscribeToCloverOAuthCompletion(
                (completion) => {
                    setOAuthCompletion(completion);
                    queryClient
                        .invalidateQueries({
                            queryKey: [
                                'paymentsInfrastructure',
                                'cloverOAuthStatus',
                                organizationUuid,
                            ],
                        })
                        .catch(() => undefined);
                    queryClient
                        .invalidateQueries({
                            queryKey: [
                                'paymentsInfrastructure',
                                'cloverInfrastructure',
                                organizationUuid,
                            ],
                        })
                        .catch(() => undefined);
                },
                {
                    openChannel: (name) => new BroadcastChannel(name),
                    window,
                    origin: window.location.origin,
                }
            ),
        [organizationUuid, queryClient]
    );
    const oauthStatusQuery = useQuery<CloverOAuthAuthorizationStatus>({
        queryKey: ['paymentsInfrastructure', 'cloverOAuthStatus', organizationUuid],
        queryFn: async () =>
            await paymentsInfrastructureApi.getCloverOAuthAuthorizationStatus(organizationUuid),
        retry: false,
        // Returning from the Clover sign-in tab must reveal the new grant and
        // start automatic provisioning without a reload (BUG-245).
        refetchOnWindowFocus: true,
        refetchInterval: (query) =>
            query.state.data?.refresh_schedule.state ===
            CloverOAuthRefreshScheduleState.SCHEDULING_PENDING
                ? 5_000
                : false,
    });
    // A renewal that Clover refuses leaves the reported lease unusable until a
    // new connection replaces it (BUG-253).
    const [refusedLease, setRefusedLease] = useState<{
        readonly accessTokenExpiresTs: string | null;
    } | null>(null);
    useEffect(() => {
        setRefusedLease(null);
    }, [organizationUuid]);
    const grantUsable =
        oauthStatusQuery.data?.state === CloverOAuthAuthorizationState.ACTIVE &&
        (refusedLease === null ||
            refusedLease.accessTokenExpiresTs !== oauthStatusQuery.data.access_token_expires_ts);
    const readinessQuery = useQuery<CloverReadinessResult>({
        queryKey: [
            'paymentsInfrastructure',
            'cloverReadiness',
            organizationUuid,
            selectedTerminalDeviceUuid ?? 'none',
        ],
        queryFn: async () => {
            if (selectedTerminalDeviceUuid === null) {
                throw new Error('A terminal device must be selected for Clover readiness.');
            }
            return await paymentsInfrastructureApi.getCloverReadiness(
                organizationUuid,
                selectedTerminalDeviceUuid
            );
        },
        enabled:
            readinessEnabled &&
            terminalSelectionEnabled &&
            selectedTerminalDeviceUuid !== null &&
            grantUsable,
        retry: false,
    });
    const [refreshIntervalDraft, setRefreshIntervalDraft] = useState<number | null>(null);
    const initialAuthorizationPath = `/api/clover/oauth/authorize/organization/launch?${new URLSearchParams(
        { organization_uuid: organizationUuid }
    ).toString()}`;
    const infrastructureQueryKey = [
        'paymentsInfrastructure',
        'cloverInfrastructure',
        organizationUuid,
    ] as const;
    const infrastructureQuery = useQuery<CloverPaymentsInfrastructureStatus>({
        queryKey: infrastructureQueryKey,
        queryFn: async () =>
            await paymentsInfrastructureApi.getCloverPaymentsInfrastructureStatus(organizationUuid),
        enabled: readinessEnabled && grantUsable,
        retry: false,
        refetchOnWindowFocus: true,
    });
    const readinessHeadingId = useId();
    const selectedTerminal = infrastructureQuery.data?.terminals.find(
        (terminal) => terminal.terminal_device_uuid === selectedTerminalDeviceUuid
    );
    const selectedTerminalLabel =
        selectedTerminal === undefined
            ? 'the selected terminal'
            : getCloverTerminalLabel(selectedTerminal);
    // A terminal Clover no longer reports cannot stay selected.
    useEffect(() => {
        if (
            selectedTerminalDeviceUuid !== null &&
            infrastructureQuery.data !== undefined &&
            selectedTerminal === undefined
        )
            setSelectedTerminalDeviceUuid(null);
    }, [infrastructureQuery.data, selectedTerminal, selectedTerminalDeviceUuid]);
    const synchronizeMutation = useMutation<
        CloverPaymentsInfrastructureSynchronizationResult,
        Error
    >({
        mutationFn: async () =>
            await paymentsInfrastructureApi.synchronizeCloverPaymentsInfrastructure(
                organizationUuid
            ),
        onSuccess: (synchronization) => {
            queryClient.setQueryData(infrastructureQueryKey, synchronization.status);
            readinessQuery.refetch().catch(() => undefined);
        },
    });
    // The first successful connection creates the Clover terminals as soon as
    // an administrator's card observes the new grant.
    const automaticProvisioningOrganizationUuid = useRef<string | null>(null);
    useEffect(() => {
        // A later connection (after a disconnection) provisions again; a failed
        // attempt stays NOT_PROVISIONED and is not retried automatically.
        if (
            oauthStatusQuery.data !== undefined &&
            (oauthStatusQuery.data.state !== CloverOAuthAuthorizationState.ACTIVE ||
                infrastructureQuery.data?.state === CloverPaymentsInfrastructureState.PROVISIONED)
        ) {
            automaticProvisioningOrganizationUuid.current = null;
        }
    }, [oauthStatusQuery.data, infrastructureQuery.data?.state]);
    useEffect(() => {
        if (
            !renewalEnabled ||
            infrastructureQuery.data?.state !== CloverPaymentsInfrastructureState.NOT_PROVISIONED ||
            synchronizeMutation.isPending ||
            automaticProvisioningOrganizationUuid.current === organizationUuid
        )
            return;
        automaticProvisioningOrganizationUuid.current = organizationUuid;
        synchronizeMutation.mutate();
    }, [renewalEnabled, infrastructureQuery.data?.state, organizationUuid, synchronizeMutation]);
    const renewMutation = useMutation<RenewCloverOAuthAuthorizationResponse, Error>({
        mutationFn: async () =>
            await paymentsInfrastructureApi.renewCloverOAuthAuthorization(organizationUuid),
        onSuccess: () => {
            oauthStatusQuery.refetch().catch(() => undefined);
            readinessQuery.refetch().catch(() => undefined);
        },
    });
    const invalidateMutation = useMutation<CloverOAuthAuthorizationStatus, Error>({
        mutationFn: async () =>
            await paymentsInfrastructureApi.invalidateCloverOAuthAuthorization(organizationUuid),
        onSuccess: (status) => {
            queryClient.setQueryData(
                ['paymentsInfrastructure', 'cloverOAuthStatus', organizationUuid],
                status
            );
            readinessQuery.refetch().catch(() => undefined);
        },
    });
    const refreshIntervalMutation = useMutation<CloverOAuthAuthorizationStatus, Error>({
        mutationFn: async () =>
            await paymentsInfrastructureApi.updateCloverOAuthRefreshInterval({
                organization_uuid: organizationUuid,
                refresh_interval_minutes:
                    refreshIntervalDraft ??
                    oauthStatusQuery.data?.refresh_interval_minutes ??
                    CloverOAuthRefreshIntervalMinutes.DEFAULT,
            }),
        onSuccess: (status) => {
            queryClient.setQueryData(
                ['paymentsInfrastructure', 'cloverOAuthStatus', organizationUuid],
                status
            );
            setRefreshIntervalDraft(null);
        },
        onError: () => setRefreshIntervalDraft(null),
    });
    const renewAuthorization = (): void => {
        const renewedLease = {
            accessTokenExpiresTs: oauthStatusQuery.data?.access_token_expires_ts ?? null,
        };
        renewMutation.mutate(undefined, {
            onSuccess: (renewal) => {
                switch (renewal.renewal_outcome) {
                    case 'REFRESHED':
                        synchronizeMutation.mutate();
                        return;
                    case 'REAUTHORIZATION_REQUIRED':
                        // Clover needs an interactive connection: offer Connect Clover.
                        setRefusedLease(renewedLease);
                        return;
                }
            },
        });
    };

    return (
        <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">Clover readiness</p>
            {oauthCompletion !== null && (
                <Alert
                    variant={
                        oauthCompletion.result === CloverOAuthCompletionResult.CONNECTED
                            ? 'default'
                            : 'destructive'
                    }
                    data-clover-oauth-completion={oauthCompletion.result}
                >
                    <AlertDescription>
                        {describeCloverOAuthCompletion(oauthCompletion)}
                    </AlertDescription>
                </Alert>
            )}
            {unavailableReason !== undefined && (
                <p className="text-sm text-muted-foreground">{unavailableReason}</p>
            )}
            {oauthStatusQuery.isPending ? (
                <p className="text-sm" role="status">
                    Loading Clover authorization lease...
                </p>
            ) : oauthStatusQuery.isError ? (
                <Alert variant="destructive">
                    <AlertDescription>Unable to load Clover authorization lease.</AlertDescription>
                </Alert>
            ) : (
                <div className="space-y-1 rounded border p-2">
                    <p className="text-sm font-medium">OAuth authorization</p>
                    <p className="text-sm">
                        Status: {CloverOAuthAuthorizationStateLabels[oauthStatusQuery.data.state]}
                    </p>
                    <p className="text-sm">
                        {(() => {
                            switch (oauthStatusQuery.data.refresh_schedule.state) {
                                case CloverOAuthRefreshScheduleState.SCHEDULED:
                                    return `Next scheduled refresh: ${formatOAuthExpiration(
                                        oauthStatusQuery.data.refresh_schedule
                                            .next_refresh_executes_ts,
                                        oauthStatusQuery.data.refresh_schedule.merchant_timezone
                                    )}`;
                                case CloverOAuthRefreshScheduleState.NOT_CONNECTED:
                                    return 'Next scheduled refresh: unavailable until Clover is connected.';
                                case CloverOAuthRefreshScheduleState.INACTIVE:
                                    return 'Next scheduled refresh: unavailable while authorization is inactive.';
                                case CloverOAuthRefreshScheduleState.REAUTHORIZATION_REQUIRED:
                                    return 'Next scheduled refresh: unavailable until Clover is reauthorized.';
                                case CloverOAuthRefreshScheduleState.SCHEDULING_PENDING:
                                    return 'Next scheduled refresh: scheduling pending.';
                            }
                        })()}
                    </p>
                    {refreshIntervalEditingEnabled && (
                        <div className="space-y-2 rounded border p-2">
                            <label
                                className="text-sm font-medium"
                                htmlFor="clover-refresh-interval"
                            >
                                Refresh before token expiry (minutes)
                            </label>
                            <input
                                id="clover-refresh-interval"
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                type="number"
                                min={CloverOAuthRefreshIntervalMinutes.MINIMUM}
                                max={CloverOAuthRefreshIntervalMinutes.MAXIMUM}
                                step={1}
                                value={
                                    refreshIntervalDraft ??
                                    oauthStatusQuery.data.refresh_interval_minutes
                                }
                                onChange={(event) =>
                                    setRefreshIntervalDraft(event.currentTarget.valueAsNumber)
                                }
                            />
                            <Button
                                type="button"
                                variant="outline"
                                disabled={
                                    refreshIntervalMutation.isPending ||
                                    refreshIntervalDraft === null ||
                                    !Number.isInteger(refreshIntervalDraft) ||
                                    refreshIntervalDraft <
                                        CloverOAuthRefreshIntervalMinutes.MINIMUM ||
                                    refreshIntervalDraft >
                                        CloverOAuthRefreshIntervalMinutes.MAXIMUM ||
                                    refreshIntervalDraft ===
                                        oauthStatusQuery.data.refresh_interval_minutes
                                }
                                onClick={() => refreshIntervalMutation.mutate()}
                            >
                                {refreshIntervalMutation.isPending
                                    ? 'Saving refresh interval...'
                                    : 'Save refresh interval'}
                            </Button>
                            {refreshIntervalDraft !== null &&
                                (!Number.isInteger(refreshIntervalDraft) ||
                                    refreshIntervalDraft <
                                        CloverOAuthRefreshIntervalMinutes.MINIMUM ||
                                    refreshIntervalDraft >
                                        CloverOAuthRefreshIntervalMinutes.MAXIMUM) && (
                                    <p className="text-sm text-destructive" role="alert">
                                        Enter a whole number from 1 to 43,200 minutes.
                                    </p>
                                )}
                            {refreshIntervalMutation.isError && (
                                <p className="text-sm text-destructive" role="alert">
                                    Unable to save the Clover refresh interval.
                                </p>
                            )}
                        </div>
                    )}
                    {grantUsable ? (
                        <>
                            <p className="text-sm">
                                Access token expires:{' '}
                                {formatOAuthExpiration(
                                    oauthStatusQuery.data.access_token_expires_ts,
                                    oauthStatusQuery.data.merchant_timezone
                                )}
                            </p>
                            <p className="text-sm">
                                Authorization lease expires:{' '}
                                {formatOAuthExpiration(
                                    oauthStatusQuery.data.refresh_token_expires_ts,
                                    oauthStatusQuery.data.merchant_timezone
                                )}
                            </p>
                            <p className="text-sm text-muted-foreground">
                                Anchorbase renews the access token automatically when Clover access
                                is needed.
                            </p>
                            <Button
                                type="button"
                                variant="destructive"
                                disabled={!invalidationEnabled || invalidateMutation.isPending}
                                onClick={() => setInvalidationConfirmationOpen(true)}
                            >
                                {invalidateMutation.isPending
                                    ? 'Invalidating Clover authorization...'
                                    : 'Invalidate Clover authorization'}
                            </Button>
                        </>
                    ) : (
                        <>
                            {oauthStatusQuery.data.state ===
                                CloverOAuthAuthorizationState.NOT_CONNECTED ||
                            oauthStatusQuery.data.state ===
                                CloverOAuthAuthorizationState.DISCONNECTED ? (
                                <>
                                    <p className="text-sm text-muted-foreground">
                                        No Clover authorization is connected.
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        Choose Connect Clover to sign in to Clover and authorize the
                                        connection. Anchorbase then sets up this organization's
                                        Clover terminals automatically. You do not need to launch
                                        Anchorbase from the Clover App Marketplace first.
                                    </p>
                                </>
                            ) : (
                                <p className="text-sm text-muted-foreground">
                                    The stored Clover authorization is not usable. Choose Connect
                                    Clover to sign in to Clover again; Anchorbase keeps this
                                    organization connected to the same Clover merchant.
                                </p>
                            )}
                            {authorizationNavigationEnabled && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    data-clover-initial-authorization="true"
                                    onClick={() => setConfirmationOpen(true)}
                                >
                                    Connect Clover
                                </Button>
                            )}
                        </>
                    )}
                    {grantUsable && (
                        <Button
                            type="button"
                            variant="outline"
                            disabled={
                                !renewalEnabled ||
                                renewMutation.isPending ||
                                synchronizeMutation.isPending
                            }
                            onClick={renewAuthorization}
                        >
                            {renewMutation.isPending || synchronizeMutation.isPending
                                ? 'Refreshing Clover connection...'
                                : 'Refresh Clover Connection'}
                        </Button>
                    )}
                    {readinessEnabled && grantUsable && (
                        <CloverPaymentsInfrastructureSummary
                            status={infrastructureQuery.data}
                            isError={infrastructureQuery.isError}
                            synchronizing={synchronizeMutation.isPending}
                            synchronization={synchronizeMutation.data}
                            synchronizationError={synchronizeMutation.error}
                            readinessCheckEnabled={terminalSelectionEnabled}
                            checkedTerminalDeviceUuid={selectedTerminalDeviceUuid}
                            onCheckReadiness={(terminalDeviceUuid) => {
                                if (terminalDeviceUuid === selectedTerminalDeviceUuid) {
                                    readinessQuery.refetch().catch(() => undefined);
                                    return;
                                }
                                setSelectedTerminalDeviceUuid(terminalDeviceUuid);
                            }}
                        />
                    )}
                    {renewMutation.isError && (
                        <p className="text-sm text-destructive" role="alert">
                            {cloverServerMessage(renewMutation.error) ??
                                'Unable to renew Clover authorization.'}{' '}
                            The existing Clover authorization was not invalidated.
                        </p>
                    )}
                    {invalidateMutation.isError && (
                        <p className="text-sm text-destructive" role="alert">
                            Unable to invalidate Clover authorization.
                        </p>
                    )}
                </div>
            )}
            {invalidationEnabled && (
                <CloverInvalidationConfirmationDialog
                    open={invalidationConfirmationOpen}
                    onOpenChange={setInvalidationConfirmationOpen}
                    onConfirm={() => invalidateMutation.mutate()}
                />
            )}
            {authorizationNavigationEnabled && (
                <CloverOAuthConfirmationDialog
                    open={confirmationOpen}
                    onOpenChange={setConfirmationOpen}
                    organizationUuid={organizationUuid}
                    organizationName={organizationName}
                    authorizationPath={initialAuthorizationPath}
                    openInNewWindow={true}
                />
            )}
            {selectedTerminalDeviceUuid === null ? null : (
                <section
                    className="space-y-2"
                    aria-labelledby={readinessHeadingId}
                    data-clover-readiness-panel={selectedTerminalDeviceUuid}
                >
                    <p className="text-sm font-medium" id={readinessHeadingId}>
                        Readiness for {selectedTerminalLabel}
                    </p>
                    {!grantUsable ? (
                        <p className="text-sm text-muted-foreground">
                            Connect Clover before checking readiness.
                        </p>
                    ) : !readinessEnabled ? (
                        <p className="text-sm text-muted-foreground">
                            Clover payment readiness is unavailable in the active role.
                        </p>
                    ) : readinessQuery.isPending ? (
                        <p className="text-sm" role="status">
                            Checking Clover readiness...
                        </p>
                    ) : readinessQuery.isError ? (
                        <Alert variant="destructive">
                            <AlertDescription>
                                <p>
                                    {(readinessQuery.error instanceof ApiError &&
                                    readinessQuery.error.status === 400
                                        ? cloverServerMessage(readinessQuery.error)
                                        : undefined) ?? 'Unable to check Clover readiness.'}
                                </p>
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="mt-2"
                                    onClick={() => void readinessQuery.refetch()}
                                >
                                    Retry readiness check
                                </Button>
                                {readinessQuery.error instanceof ApiError &&
                                    readinessQuery.error.status === 400 &&
                                    readinessQuery.error.message.startsWith(
                                        'Clover OAuth authorization '
                                    ) && (
                                        <p className="mt-2 text-sm">
                                            Use Refresh Clover Connection above to continue through
                                            Anchorbase.
                                        </p>
                                    )}
                            </AlertDescription>
                        </Alert>
                    ) : (
                        <div className="space-y-3">
                            {readinessQuery.isFetching && (
                                <p className="text-sm" role="status">
                                    Refreshing Clover readiness...
                                </p>
                            )}
                            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                {CloverReadinessCheckDefinitions.map((definition) => {
                                    // A virtual terminal reports no Cloud Pay device (BUG-256).
                                    const check = readinessQuery.data[definition.key];
                                    return check === undefined ? null : (
                                        <CloverReadinessCheckSummary
                                            key={definition.key}
                                            label={definition.label}
                                            check={check}
                                        />
                                    );
                                })}
                                {readinessQuery.data.surcharge !== undefined && (
                                    <CloverSurchargeReadinessSummary
                                        surchargeReadiness={readinessQuery.data.surcharge}
                                    />
                                )}
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                disabled={readinessQuery.isFetching}
                                onClick={() => void readinessQuery.refetch()}
                            >
                                Retry readiness check
                            </Button>
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}
